import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import type { Brand } from "@/components/AppSidebar";
import { ExtendedTable } from "@/components/ExtendedTable";
import SearchExportGenerate from "@/components/SearchExportGenerate";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  competitorsAPI,
  onboardingGuideAPI,
  visabilityAPI,
  type PaginatedResponseMeta,
} from "@/services/api";
import {
  ActiveCompetitorsColumns,
  getSuggestedCompetitorsColumns,
} from "@/services/helpers";
import { canonicalizeDomain } from "@/utils/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useReactTable,
  type ColumnFiltersState,
  type PaginationState,
  type SortingState,
  type Table,
} from "@tanstack/react-table";
import {
  ArrowRight,
  Check,
  LoaderCircle,
  Sparkles,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";
import AddCompetitorDialog from "@/components/AddCompetitorDialog";
import ActionsSuggested from "@/components/ActionsSuggested";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useDataStore } from "@/store/useDataStore";
import { useCompetitorsRealtime } from "@/hooks/useCompetitorsRealtime";
import { useVisibilityRealtimeInvalidation } from "@/hooks/useVisibilityRealtimeInvalidation";
import {
  capturePosthogEvent,
  getTimeSinceBrandOnboardingSeconds,
  isFirstDashboardView,
} from "@/lib/posthog";
import { useAuth } from "@/components/AuthContext";

const defaultPaginationMeta: PaginatedResponseMeta = {
  page: 1,
  pageSize: 25,
  total: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};
const SUGGESTED_COMPETITORS_PAGE_SIZE = 25;
const suggestedDefaultCompetitorsResponse: {
  ok: boolean;
  suggested: any[];
  pagination: PaginatedResponseMeta;
} = {
  ok: true,
  suggested: [],
  pagination: defaultPaginationMeta,
};
const defaultData: any[] = [];
const defaultCompetitorsResponse = {
  ok: true,
  competitors: defaultData,
};

const normalizeCompetitorsData = (payload: unknown): any[] => {
  if (Array.isArray(payload)) return payload;
  if (
    payload &&
    typeof payload === "object" &&
    Array.isArray((payload as { competitors?: unknown }).competitors)
  ) {
    return (payload as { competitors: any[] }).competitors;
  }
  return [];
};

const CompetitorsPage = () => {
  const { t } = useLocale();
  const { user } = useAuth();
  const { currentBrand, selectedProviders, timeRange } = useOutletContext<{
    currentBrand: Brand;
    isTrialEnded: boolean;
    selectedCountries: string[];
    selectedProviders?: string[];
    timeRange?: string;
  }>();
  const brandId = currentBrand?.brandId || currentBrand?.id || "";
  const normalizedSelectedProviders = useMemo(() => {
    if (!selectedProviders?.length) return undefined;
    const providers = Array.from(
      new Set(
        selectedProviders
          .map((provider) =>
            String(provider || "")
              .trim()
              .toUpperCase(),
          )
          .filter(Boolean),
      ),
    ).sort();
    return providers.length ? providers : undefined;
  }, [selectedProviders]);
  const providersKey = normalizedSelectedProviders?.join(",") || "all";
  const normalizedDays = Number.parseInt(String(timeRange || "7d"), 10) || 7;
  const canonicalBrandDomain = useMemo(
    () => canonicalizeDomain(currentBrand?.url),
    [currentBrand?.url],
  );

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const { competitorsApproved, setCompetitorsApproved } = useDataStore();

  const [openAddPromptDialog, setOpenAddPromptDialog] = useState(false);
  const [suggestedPromptsRowSelection, setSuggestedPromptsRowSelection] =
    useState({});
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const initialTab = searchParams.get("tab");
    return initialTab === "suggested" ? "suggested" : "active";
  });
  const [activePagination, setActivePagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [suggestedPagination, setSuggestedPagination] =
    useState<PaginationState>({
      pageIndex: 0,
      pageSize: SUGGESTED_COMPETITORS_PAGE_SIZE,
    });
  const suggestedSearch = search.trim();
  const [dashboardTrackedKey, setDashboardTrackedKey] = useState("");
  const competitorsQueryKey = [
    "competitors",
    brandId,
    providersKey,
    normalizedDays,
  ];
  const fetchCompetitors = async () =>
    competitorsAPI.getCompetitors(
      brandId,
      normalizedSelectedProviders,
      normalizedDays,
    );

  const completeCompetitorsStep = async (method: "manual_add") => {
    if (!brandId) return;
    try {
      const guideRes = await onboardingGuideAPI.updateState({
        brandId,
        stepName: "activate_competitors",
        completionSource: "user",
      });
      if (!guideRes.completedStepName) return;
      capturePosthogEvent("onboarding_guide_step_completed", {
        step_name: guideRes.completedStepName,
        completion_source: guideRes.completionSource || "user",
      });
      capturePosthogEvent("onboarding_step_completed", {
        step: "competitors",
        method,
      });
      queryClient.invalidateQueries({
        queryKey: ["onboardingGuide", brandId],
      });
    } catch {
      // ignore onboarding tracking failures
    }
  };

  const invalidateCompetitorDependentQueries = () => {
    queryClient.invalidateQueries({
      predicate: (query: { queryKey: any }) => {
        const rootKey = query?.queryKey?.[0];
        return [
          "competitors",
          "dashboardCompetitors",
          "competitorsMetricsFallback",
          "visability",
          "visibility",
        ].includes(rootKey);
      },
    });
  };

  //---start-requests
  const {
    data: competitorsResponse = defaultCompetitorsResponse,
    isLoading: isCompetitorsLoading,
  } = useQuery({
    queryKey: competitorsQueryKey,
    queryFn: fetchCompetitors,
    enabled: !!brandId,
    staleTime: Infinity,
  });

  const data = useMemo(
    () => normalizeCompetitorsData(competitorsResponse),
    [competitorsResponse],
  );
  useCompetitorsRealtime({
    brandId,
    status: "tracked",
    providers: normalizedSelectedProviders,
    enabled: !!brandId,
    queryKey: competitorsQueryKey,
  });
  const shouldLoadCompetitorsMetricsFallback =
    !!brandId && !isCompetitorsLoading && data.length > 0;

  const {
    data: competitorsMetricsFallback = { visibility: {}, position: {} },
    isLoading: isCompetitorsMetricsFallbackLoading,
    isError: isCompetitorsMetricsFallbackError,
  } = useQuery({
    queryKey: [
      "competitorsMetricsFallback",
      brandId,
      providersKey,
      normalizedDays,
    ],
    queryFn: async () => {
      if (!brandId) return { visibility: {}, position: {} };
      const res = await visabilityAPI.getRunResults(
        { ...(currentBrand || {}), brandId } as Brand,
        normalizedDays,
        normalizedSelectedProviders,
        undefined,
        false,
        false,
        undefined,
        false,
        false,
      );
      return {
        visibility: res?.data?.competitorsVisibility || {},
        position: res?.data?.competitorsPosition || {},
      };
    },
    enabled: shouldLoadCompetitorsMetricsFallback,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const competitorsMetricsFallbackQueryKey = useMemo(
    () => ["competitorsMetricsFallback", brandId, providersKey, normalizedDays],
    [brandId, providersKey, normalizedDays],
  );
  useVisibilityRealtimeInvalidation({
    brandId,
    days: normalizedDays,
    providers: normalizedSelectedProviders,
    queryKeys: [competitorsMetricsFallbackQueryKey],
    enabled: shouldLoadCompetitorsMetricsFallback,
  });
  const isCompetitorsMetricsPending =
    shouldLoadCompetitorsMetricsFallback &&
    isCompetitorsMetricsFallbackLoading &&
    !isCompetitorsMetricsFallbackError;
  const shouldUseDayWindowMetrics =
    shouldLoadCompetitorsMetricsFallback &&
    !isCompetitorsMetricsPending &&
    !isCompetitorsMetricsFallbackError;

  const {
    data: suggestedCompetitorsResponse = suggestedDefaultCompetitorsResponse,
    isLoading: isSuggestedCompetitorsLoading,
  } = useQuery({
    queryKey: [
      "suggestedCompetitors",
      brandId,
      providersKey,
      normalizedDays,
      suggestedPagination.pageIndex,
      suggestedPagination.pageSize,
      suggestedSearch,
    ],
    queryFn: async () => {
      return competitorsAPI.getSuggestedCompetitors(
        brandId,
        normalizedSelectedProviders,
        normalizedDays,
        {
          page: suggestedPagination.pageIndex + 1,
          pageSize: suggestedPagination.pageSize,
          search: suggestedSearch,
        },
      );
    },
    enabled: !!brandId,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  const suggestedCompetitorsData = suggestedCompetitorsResponse.suggested || [];
  const suggestedPaginationMeta =
    suggestedCompetitorsResponse.pagination || defaultPaginationMeta;

  //---end-requests
  //--start--all mutations
  const deleteMutation = useMutation({
    mutationFn: async ({ id, approved }: { id: string; approved: boolean }) => {
      const res = await competitorsAPI.approveCompetitor(approved, id);
      return res.prompts;
    },
  });

  const addMutation = useMutation({
    mutationFn: async (competitor: any) => {
      const res = await competitorsAPI.addCompetitor(competitor);
      return res.prompts;
    },
  });
  //---end--all mutations
  //---start-data-filtering
  const filteredActiveData = useMemo(() => {
    const aliasMap = new Map<string, string>();
    const normalizeKey = (value: string) =>
      value
        .toLowerCase()
        .replace(/[^a-z0-9.]+/g, " ")
        .trim();
    const addAlias = (key?: string | null, domain?: string | null) => {
      if (!key || !domain) return;
      const normalizedDomain =
        canonicalizeDomain(domain) || domain.toLowerCase();
      const lowered = String(key).toLowerCase();
      aliasMap.set(lowered, normalizedDomain);
      const canonicalKey = canonicalizeDomain(lowered);
      if (canonicalKey) {
        aliasMap.set(canonicalKey, normalizedDomain);
      }
      const normalizedAlias = normalizeKey(lowered);
      if (normalizedAlias) {
        aliasMap.set(normalizedAlias, normalizedDomain);
        const firstToken = normalizedAlias.split(" ")[0];
        if (firstToken) {
          aliasMap.set(firstToken, normalizedDomain);
        }
      }
      const stem = normalizedDomain.split(".")[0];
      if (stem) {
        aliasMap.set(stem.toLowerCase(), normalizedDomain);
      }
    };
    if (canonicalBrandDomain) {
      addAlias(canonicalBrandDomain, canonicalBrandDomain);
      addAlias(currentBrand?.name, canonicalBrandDomain);
      addAlias(currentBrand?.url, canonicalBrandDomain);
    }
    data.forEach((competitor: any) => {
      const domain = canonicalizeDomain(competitor?.domain);
      if (!domain) return;
      addAlias(competitor?.domain, domain);
      addAlias(competitor?.brandKey, domain);
      addAlias(competitor?.brandDisplay, domain);
      addAlias(competitor?.name, domain);
    });
    const fallbackVisibilityByDomain = new Map<string, number>();
    Object.entries(competitorsMetricsFallback?.visibility || {}).forEach(
      ([key, value]) => {
        const numeric = Number(value);
        if (!Number.isFinite(numeric) || numeric < 0) return;
        const lowered = String(key || "").toLowerCase();
        const normalized = normalizeKey(lowered);
        const resolved =
          aliasMap.get(lowered) ||
          (normalized
            ? aliasMap.get(normalized) || aliasMap.get(normalized.split(" ")[0])
            : undefined) ||
          canonicalizeDomain(lowered);
        if (!resolved) return;
        fallbackVisibilityByDomain.set(resolved, numeric);
      },
    );

    const fallbackPositionByDomain = new Map<string, number>();
    Object.entries(competitorsMetricsFallback?.position || {}).forEach(
      ([key, value]) => {
        const numeric = Number(value);
        if (!Number.isFinite(numeric) || numeric <= 0) return;
        const lowered = String(key || "").toLowerCase();
        const normalized = normalizeKey(lowered);
        const resolved =
          aliasMap.get(lowered) ||
          (normalized
            ? aliasMap.get(normalized) || aliasMap.get(normalized.split(" ")[0])
            : undefined) ||
          canonicalizeDomain(lowered);
        if (!resolved) return;
        fallbackPositionByDomain.set(resolved, numeric);
      },
    );

    const uniqueByDomain = new Map<string, any>();

    data.forEach((competitor: any) => {
      const canonical = canonicalizeDomain(competitor.domain);
      if (!canonical) return;
      if (!uniqueByDomain.has(canonical)) {
        const rawVisibility =
          typeof competitor.visibility === "number"
            ? competitor.visibility
            : Number(competitor.visibility);
        const fallbackVisibility = fallbackVisibilityByDomain.get(canonical);
        const visibility = shouldUseDayWindowMetrics
          ? Number.isFinite(fallbackVisibility || NaN)
            ? Number(fallbackVisibility)
            : 0
          : Number.isFinite(rawVisibility)
            ? rawVisibility
            : 0;
        let position: number | null = null;
        if (competitor.position !== null && competitor.position !== undefined) {
          const rawPosition =
            typeof competitor.position === "number"
              ? competitor.position
              : Number(competitor.position);
          position = Number.isFinite(rawPosition) ? rawPosition : null;
        }
        const fallback = fallbackPositionByDomain.get(canonical);
        if (shouldUseDayWindowMetrics) {
          position = Number.isFinite(fallback || NaN) ? Number(fallback) : null;
        } else if (Number.isFinite(fallback || NaN)) {
          position = Number(fallback);
        }

        uniqueByDomain.set(canonical, {
          ...competitor,
          domain: canonical,
          visibility,
          position,
          isMetricsPending: isCompetitorsMetricsPending,
          isOwnBrand: canonical === canonicalBrandDomain,
        });
      }
    });

    return Array.from(uniqueByDomain.values()).filter((competitor) =>
      (competitor.domain || "")
        .toLowerCase()
        .includes(search.trim().toLowerCase()),
    );
  }, [
    data,
    search,
    canonicalBrandDomain,
    competitorsMetricsFallback,
    shouldUseDayWindowMetrics,
    currentBrand?.name,
    currentBrand?.url,
  ]);

  const activeDomains = useMemo(() => {
    const set = new Set<string>();
    filteredActiveData.forEach((competitor: { domain: string }) => {
      if (competitor.domain) set.add(competitor.domain);
    });
    return set;
  }, [filteredActiveData]);

  const filteredSuggestedData = useMemo(() => {
    return suggestedCompetitorsData
      .map((competitor: { domain: string }) => {
        const domain = String(competitor?.domain || "");
        const canonical = canonicalizeDomain(domain);
        return {
          ...competitor,
          domain,
          isOwnBrand: Boolean(canonical && canonical === canonicalBrandDomain),
        };
      })
      .filter((competitor: { domain: string }) => {
        const domain = String(competitor?.domain || "");
        const canonical = canonicalizeDomain(domain);
        if (canonical && activeDomains.has(canonical)) return false;
        return true;
      });
  }, [suggestedCompetitorsData, activeDomains, canonicalBrandDomain]);
  const shouldShowSuggestedLoader =
    isSuggestedCompetitorsLoading && suggestedCompetitorsData.length === 0;
  const shouldShowActiveTableLoader =
    addMutation.isPending ||
    deleteMutation.isPending ||
    (isCompetitorsLoading && filteredActiveData.length === 0);
  const shouldShowSuggestedTableLoader =
    addMutation.isPending ||
    deleteMutation.isPending ||
    shouldShowSuggestedLoader;
  //---end-data-filtering
  //---start-handlers
  const handleDeleteSelected = async (
    table: Table<any>,
    action: "inactive" | "suggested",
    competitorId: string,
    approved: boolean,
  ) => {
    let selectedCompetitors: string[] = [];
    if (!competitorId) {
      const selectedRows = table.getSelectedRowModel().rows;
      selectedCompetitors = selectedRows.map((row) => row.original.id);
    } else {
      selectedCompetitors = [competitorId];
    }

    try {
      await Promise.all(
        selectedCompetitors.map((id: string) =>
          deleteMutation.mutateAsync({ id: id, approved: approved }),
        ),
      );

      invalidateCompetitorDependentQueries();
      if (action === "suggested") {
        queryClient.invalidateQueries({ queryKey: ["suggestedCompetitors"] });
      }
      if (approved && selectedCompetitors.length) {
        await completeCompetitorsStep("manual_add");
      }
      table.resetRowSelection();
      toast.success(`The competitors(s) ${approved ? "approved" : "deleted"}`, {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <Trash2 className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
    } catch (err) {
      table.resetRowSelection();
      toast.error(`Couldn't ${approved ? "approv" : "delet"} competitors(s)!`, {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <TriangleAlert className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
    }
  };

  const handleAdd = async (competitor: any | null) => {
    if (!brandId) return;
    let selectedCompetitors: any[] = [];

    if (!competitor) {
      const selectedRows = suggestedCompetitorsTable.getSelectedRowModel().rows;
      selectedCompetitors = selectedRows.map((row) => {
        let competitor: any = {
          domain: row.original.domain,
          name: row.original.brandDisplay,
          brandId,
        };
        return competitor;
      });
    } else {
      let currCompetitor: any = {
        domain: competitor.url,
        name: competitor.text,
        brandId,
      };
      selectedCompetitors = [currCompetitor];
    }

    try {
      await Promise.all(
        selectedCompetitors.map((competitor) =>
          addMutation.mutateAsync(competitor),
        ),
      );
      invalidateCompetitorDependentQueries();
      queryClient.invalidateQueries({ queryKey: ["suggestedCompetitors"] });
      suggestedCompetitorsTable.resetRowSelection();
      if (selectedCompetitors.length) {
        await completeCompetitorsStep("manual_add");
      }
      toast.success(t("legacy.the.competitor.s.has.been.added.10f90bd"), {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <Check className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
    } catch (err: any) {
      suggestedCompetitorsTable.resetRowSelection();
      toast.error(err.response?.data.message || "Couldn't add competitor(s)!", {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <TriangleAlert className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
    }
  };
  //---end-handlers
  //---start-tables
  const table = useReactTable({
    data: filteredActiveData,
    columns: ActiveCompetitorsColumns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setActivePagination,
    autoResetPageIndex: false,
    enableRowSelection: (row) => !row.original?.isOwnBrand,
    state: {
      pagination: activePagination,
      sorting,
      columnFilters,
      rowSelection,
    },
  });

  useEffect(() => {
    if (
      activePagination.pageIndex > 0 &&
      table.getRowModel().rows.length === 0
    ) {
      setActivePagination((prev) => ({
        ...prev,
        pageIndex: prev.pageIndex - 1,
      }));
    }
  }, [filteredActiveData]);

  useEffect(() => {
    if (
      suggestedPagination.pageIndex > 0 &&
      suggestedPaginationMeta.totalPages > 0 &&
      suggestedPagination.pageIndex >= suggestedPaginationMeta.totalPages
    ) {
      setSuggestedPagination((prev) => ({
        ...prev,
        pageIndex: suggestedPaginationMeta.totalPages - 1,
      }));
    }
  }, [suggestedPagination.pageIndex, suggestedPaginationMeta.totalPages]);

  useEffect(() => {
    setSuggestedPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
    setSuggestedPromptsRowSelection({});
  }, [brandId, providersKey, normalizedDays, suggestedSearch]);

  const suggestedCompetitorsTable = useReactTable({
    data: filteredSuggestedData,
    columns: getSuggestedCompetitorsColumns(
      (id) =>
        handleDeleteSelected(suggestedCompetitorsTable, "suggested", id, false),
      (id) =>
        handleDeleteSelected(suggestedCompetitorsTable, "suggested", id, true),
      () => setCompetitorsApproved(competitorsApproved + 1),
    ),
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: setSuggestedPromptsRowSelection,
    manualPagination: true,
    pageCount: suggestedPaginationMeta.totalPages,
    onPaginationChange: setSuggestedPagination,
    autoResetPageIndex: false,
    state: {
      pagination: suggestedPagination,
      rowSelection: suggestedPromptsRowSelection,
    },
  });
  //---end-tables

  useEffect(() => {
    suggestedCompetitorsTable.resetRowSelection();
    table.resetRowSelection();
    setSearch("");
  }, [tab]);

  useEffect(() => {
    const requestedTab = searchParams.get("tab");
    if (requestedTab === "active" || requestedTab === "suggested") {
      setTab(requestedTab);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!brandId || isCompetitorsLoading || isCompetitorsMetricsPending) return;
    const hasData = filteredActiveData.length > 0;
    const key = `${brandId}:${hasData}`;
    if (dashboardTrackedKey === key) return;
    setDashboardTrackedKey(key);
    capturePosthogEvent("dashboard_viewed", {
      brand_id: brandId,
      dashboard: "competitors",
      is_first_view: isFirstDashboardView({
        userId: user?.id,
        brandId,
        dashboard: "competitors",
      }),
      has_data: hasData,
      time_since_onboarding_seconds:
        getTimeSinceBrandOnboardingSeconds(brandId) ?? undefined,
    });
  }, [
    brandId,
    dashboardTrackedKey,
    filteredActiveData.length,
    isCompetitorsLoading,
    isCompetitorsMetricsPending,
    user?.id,
  ]);

  return (
    <div className="relative flex-1">
      <div className="text-sm text-muted-foreground mb-3">
        <LocalizedText id="legacy.manage.and.organize.your.competitors.1j3c6cu" />
      </div>
      <div className="w-full rounded-md border p-4 mb-15">
        <div className="flex-col gap-4 items-start xl:flex-row flex xl:items-center justify-between pb-4">
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <div className="flex justify-between w-full items-center flex-wrap gap-2">
              <TabsList>
                <TabsTrigger className="cursor-pointer" value="active">
                  <LocalizedText id="legacy.active.1530k7d" />{" "}
                  <Badge
                    className="bg-[#d1d1d1] rounded-full"
                    variant="outline"
                  >
                    {filteredActiveData.length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger
                  className="cursor-pointer"
                  value="suggested"
                  data-onboarding-target="competitors-suggested-tab"
                >
                  <LocalizedText id="legacy.suggested.vyozz4" />{" "}
                  {shouldShowSuggestedLoader ? (
                    <Badge
                      className="bg-[#d1d1d1] rounded-full h-6 w-6 p-0 flex items-center justify-center"
                      variant="outline"
                    >
                      <LoaderCircle className="h-3.5 w-3.5 animate-spin text-[#FF5E00]" />
                    </Badge>
                  ) : (
                    <Badge
                      className="bg-[#d1d1d1] rounded-full"
                      variant="outline"
                    >
                      {suggestedPaginationMeta.total}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
              <div className="flex-wrap lg:flex-nowrap flex gap-2 items-center text-sm">
                <SearchExportGenerate
                  search={search}
                  refetch={() => {}}
                  tab={tab}
                  showButton={false}
                  setSearch={setSearch}
                  data={
                    tab === "active"
                      ? filteredActiveData
                      : filteredSuggestedData
                  }
                  isGeneratedLoading={false}
                  table={tab === "active" ? table : suggestedCompetitorsTable}
                />
                {tab !== "suggested" && (
                  <AddCompetitorDialog
                    openAddPromptDialog={openAddPromptDialog}
                    setOpenAddPromptDialog={setOpenAddPromptDialog}
                    currentBrand={currentBrand}
                    addMutation={(prompt: any) => handleAdd(prompt)}
                  ></AddCompetitorDialog>
                )}
              </div>
            </div>
            <TabsContent value="active">
              {competitorsApproved < 3 && (
                <div className="flex justify-between my-3 p-3 bg-[#F5F5F5] rounded-md gap-2 items-center">
                  <div className="flex items-center text-sm gap-2">
                    <div className="p-1 bg-[#FF5E00] rounded-md text-white">
                      <Sparkles size={16} />
                    </div>
                    <div>
                      <span className="font-semibold">
                        <LocalizedText id="legacy.get.ai.suggested.competitors.qo05eo" />
                      </span>{" "}
                      <LocalizedText id="legacy.see.which.brands.are.mentioned.alongside.yours.across.1vnyxoz" />
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="cursor-pointer"
                    onClick={() => setTab("suggested")}
                  >
                    <LocalizedText id="legacy.suggested.competitors.1w3tgfn" />{" "}
                    <ArrowRight></ArrowRight>
                  </Button>
                </div>
              )}
              <ExtendedTable
                limitPrompts={null}
                pageName="competitor"
                noDataText={{
                  title:
                    !!data.length && !!search.trim().length
                      ? "All active competitors are filtered"
                      : "No active competitors added yet",
                  description:
                    !!data.length && !!search.trim().length
                      ? "Please clear the search to see all active competitors."
                      : "Add other brands to see how your visibility compares",
                }}
                action={
                  !!data.length && !!search.trim().length
                    ? null
                    : {
                        title: "Add competitor",
                        actionFn: () => setOpenAddPromptDialog(true),
                      }
                }
                loading={shouldShowActiveTableLoader}
                table={table}
                columns={7}
              ></ExtendedTable>
            </TabsContent>
            <TabsContent value="suggested">
              <ExtendedTable
                limitPrompts={null}
                pageName="competitor"
                noDataText={{
                  title: !!suggestedSearch.length
                    ? "No suggested competitors match your search"
                    : "No competitors yet",
                  description: !!suggestedSearch.length
                    ? "Please clear the search to see all suggested competitors."
                    : "You don't have any suggested competitors yet.",
                }}
                loading={shouldShowSuggestedTableLoader}
                table={suggestedCompetitorsTable}
                columns={5}
              ></ExtendedTable>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      {tab === "active" ? (
        <ActionsSuggested
          rowSelection={rowSelection}
          customAction={{ icon: <Trash2 size={16}></Trash2>, title: "Delete" }}
          table={table}
          handleStatusChange={handleAdd}
          handleDeleteSelected={handleDeleteSelected}
        ></ActionsSuggested>
      ) : (
        <ActionsSuggested
          rowSelection={suggestedPromptsRowSelection}
          table={suggestedCompetitorsTable}
          handleStatusChange={handleAdd}
          handleDeleteSelected={handleDeleteSelected}
        ></ActionsSuggested>
      )}
    </div>
  );
};

export default CompetitorsPage;
